"use client";

import { useMotionValue } from "framer-motion";
import * as React from "react";

const DEFAULT_DURATION_MS = 10_000;

function usePrefersReducedMotion() {
  const [reduced, setReduced] = React.useState(false);

  React.useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return reduced;
}

/**
 * Drives tab progress with a fixed-duration timer (default 10s per tab).
 * Returns active index, progress MotionValue (0–1), and tab selection helpers.
 */
export function useFixedTabTimer(
  tabCount: number,
  durationMs = DEFAULT_DURATION_MS
) {
  const [active, setActive] = React.useState(0);
  const progress = useMotionValue(0);
  const reducedMotion = usePrefersReducedMotion();
  const startRef = React.useRef<number | null>(null);
  const rafRef = React.useRef<number | null>(null);

  const resetTimer = React.useCallback(() => {
    startRef.current = performance.now();
    progress.set(0);
  }, [progress]);

  const selectTab = React.useCallback(
    (index: number) => {
      setActive(index);
      resetTimer();
    },
    [resetTimer]
  );

  const advanceTab = React.useCallback(() => {
    setActive((current) => (current + 1) % tabCount);
    resetTimer();
  }, [resetTimer, tabCount]);

  React.useEffect(() => {
    if (reducedMotion || tabCount <= 0) {
      progress.set(0);
      return;
    }

    resetTimer();

    const tick = (now: number) => {
      if (document.hidden) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const start = startRef.current ?? now;
      const elapsed = now - start;
      const fraction = Math.min(1, elapsed / durationMs);
      progress.set(fraction);

      if (fraction >= 1) {
        advanceTab();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [
    active,
    advanceTab,
    durationMs,
    progress,
    reducedMotion,
    resetTimer,
    tabCount,
  ]);

  return { active, progress, selectTab, advanceTab, reducedMotion };
}
